import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { formatBRL } from "../lib/constants";
import VoiceCapture from "../components/VoiceCapture";
import ConfirmVoiceSheet from "../components/ConfirmVoiceSheet";
import TransactionSheet from "../components/TransactionSheet";
import TransactionRow from "../components/TransactionRow";
import { Eye, EyeOff, Plus, TrendingUp, TrendingDown, Wallet2, ArrowUpRight, Calendar } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

export default function Dashboard() {
  const { user, api } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hidden, setHidden] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [sheetInitial, setSheetInitial] = useState(null);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [reviewPayload, setReviewPayload] = useState(null);
  const [successTick, setSuccessTick] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/transactions");
      setItems(r.data);
    } catch { toast.error("Erro ao carregar movimentações"); }
    finally { setLoading(false); }
  }, [api]);
  useEffect(() => { load(); }, [load]);

  const { income, expense, balance, monthIncome, monthExpense } = useMemo(() => {
    const now = new Date();
    const inMonth = (d) => {
      const dt = new Date(d);
      return dt.getMonth() === now.getMonth() && dt.getFullYear() === now.getFullYear();
    };
    const i = items.filter((t) => t.type === "income").reduce((s, t) => s + t.amount, 0);
    const e = items.filter((t) => t.type === "expense").reduce((s, t) => s + t.amount, 0);
    const mi = items.filter((t) => t.type === "income" && inMonth(t.date)).reduce((s, t) => s + t.amount, 0);
    const me = items.filter((t) => t.type === "expense" && inMonth(t.date)).reduce((s, t) => s + t.amount, 0);
    return { income: i, expense: e, balance: i - e, monthIncome: mi, monthExpense: me };
  }, [items]);

  const recent = items.slice(0, 5);
  const openNew = () => { setSheetInitial(null); setSheetOpen(true); };
  const openEdit = (tx) => { setSheetInitial(tx); setSheetOpen(true); };
  const onSaved = (newItem, removedId) => {
    if (removedId) setItems((prev) => prev.filter((t) => t.id !== removedId));
    else if (Array.isArray(newItem)) {
      setItems((prev) => [...newItem, ...prev]);
      setSuccessTick((v) => v + 1);
    } else if (newItem) {
      setItems((prev) => {
        const idx = prev.findIndex((t) => t.id === newItem.id);
        if (idx >= 0) { const cp = [...prev]; cp[idx] = newItem; return cp; }
        return [newItem, ...prev];
      });
    }
  };
  const mask = (v) => (hidden ? "R$ ••••" : formatBRL(v));
  const openReview = (payload) => { setReviewPayload(payload); setReviewOpen(true); };

  const monthName = new Date().toLocaleDateString("pt-BR", { month: "long" });
  const monthResult = monthIncome - monthExpense;

  return (
    <div className="max-w-md mx-auto px-5 pt-8 pb-6 space-y-6">
      {/* Header */}
      <header className="flex items-center justify-between">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-11 h-11 shrink-0 rounded-2xl bg-gradient-to-br from-emerald-400 to-emerald-600 flex items-center justify-center shadow-lg shadow-emerald-500/30">
            <span className="font-display font-black text-slate-900 text-lg">
              {(user?.name || "?").charAt(0).toUpperCase()}
            </span>
          </div>
          <div className="min-w-0">
            <p className="text-[10px] uppercase tracking-[0.18em] text-emerald-300 font-semibold">Olá</p>
            <h1 className="font-display text-xl font-bold text-white truncate">{user?.name?.split(" ")[0] || "Bem-vindo"}</h1>
          </div>
        </div>
        <button
          data-testid="toggle-privacy"
          onClick={() => setHidden((v) => !v)}
          aria-label={hidden ? "Mostrar valores" : "Ocultar valores"}
          className="w-10 h-10 rounded-full bg-slate-900/60 border border-white/10 hover:border-emerald-400/40 flex items-center justify-center text-slate-300 hover:text-white"
        >
          {hidden ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
        </button>
      </header>

      {/* Balance card */}
      <section data-testid="balance-card" className="pnc-card p-6 relative overflow-hidden">
        <div className="absolute -top-20 -right-20 w-56 h-56 rounded-full bg-emerald-500/15 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-24 -left-16 w-56 h-56 rounded-full bg-sky-500/10 blur-3xl pointer-events-none" />
        <div className="relative">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-emerald-300 font-semibold">
            <Wallet2 className="w-3.5 h-3.5" /> Saldo disponível
          </div>
          <div
            data-testid="balance-value"
            className="mt-2 font-display font-black text-[2.75rem] leading-none font-mono-nums"
            style={{ color: balance >= 0 ? "#F1F5F9" : "#FB7185" }}
          >
            {mask(balance)}
          </div>
          <p className="text-xs text-slate-400 mt-2">
            Resultado total desde o início do uso
          </p>

          <div className="mt-5 grid grid-cols-2 gap-3">
            <div className="p-3 rounded-2xl bg-emerald-500/10 border border-emerald-500/20">
              <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-emerald-300 font-semibold">
                <TrendingUp className="w-3.5 h-3.5"/>Receitas
              </div>
              <div data-testid="income-value" className="mt-1 font-mono-nums font-bold text-emerald-300 text-base">
                {mask(income)}
              </div>
            </div>
            <div className="p-3 rounded-2xl bg-rose-500/10 border border-rose-500/20">
              <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-rose-300 font-semibold">
                <TrendingDown className="w-3.5 h-3.5"/>Despesas
              </div>
              <div data-testid="expense-value" className="mt-1 font-mono-nums font-bold text-rose-300 text-base">
                {mask(expense)}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Voice capture */}
      <VoiceCapture onReview={openReview} successFlash={successTick} />

      {/* Manual add + Month summary */}
      <div className="grid grid-cols-1 gap-3">
        <button
          data-testid="add-transaction-btn"
          onClick={openNew}
          className="btn-ghost w-full py-3 rounded-2xl flex items-center justify-center gap-2 text-sm font-semibold"
        >
          <Plus className="w-4 h-4" /> Adicionar manualmente
        </button>

        <div data-testid="month-summary" className="pnc-card p-4">
          <div className="flex items-center justify-between mb-2">
            <p className="text-[10px] uppercase tracking-[0.18em] text-slate-400 font-semibold flex items-center gap-1.5">
              <Calendar className="w-3.5 h-3.5" /> Resultado de {monthName}
            </p>
            <Link to="/relatorios" className="text-[11px] text-emerald-300 hover:text-emerald-200 flex items-center gap-0.5">
              Relatórios <ArrowUpRight className="w-3 h-3" />
            </Link>
          </div>
          <div className="flex items-end justify-between">
            <div>
              <p className="font-mono-nums font-bold text-2xl" style={{ color: monthResult >= 0 ? "#34D399" : "#FB7185" }}>
                {monthResult >= 0 ? "+" : "−"} {mask(Math.abs(monthResult))}
              </p>
              <p className="text-[11px] text-slate-400 mt-1">
                {mask(monthIncome)} recebidos · {mask(monthExpense)} gastos
              </p>
            </div>
            <div className="text-right">
              <div className="w-full flex gap-0.5 h-2 rounded-full overflow-hidden bg-slate-800 min-w-[100px]">
                {(() => {
                  const tot = monthIncome + monthExpense || 1;
                  const pI = (monthIncome / tot) * 100;
                  const pE = 100 - pI;
                  return (
                    <>
                      <span style={{ width: `${pI}%` }} className="bg-emerald-400" />
                      <span style={{ width: `${pE}%` }} className="bg-rose-400" />
                    </>
                  );
                })()}
              </div>
              <p className="text-[10px] text-slate-500 mt-1">Entradas vs Saídas</p>
            </div>
          </div>
        </div>
      </div>

      {/* Recent */}
      <section data-testid="recent-section">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-display font-bold text-lg text-white">Recentes</h2>
          <Link to="/movimentacoes" className="text-xs text-emerald-300 hover:text-emerald-200 flex items-center gap-0.5">
            Ver todas <ArrowUpRight className="w-3 h-3" />
          </Link>
        </div>
        {loading ? (
          <div className="text-center text-slate-500 py-8 text-sm">Carregando…</div>
        ) : recent.length === 0 ? (
          <div className="pnc-card p-6 text-center">
            <p className="text-sm text-slate-300">Toque no microfone e diga <em className="text-emerald-300 not-italic">“café 15 reais”</em>.</p>
          </div>
        ) : (
          <div className="pnc-card p-2 stagger">
            {recent.map((tx) => <TransactionRow key={tx.id} tx={tx} onClick={openEdit} />)}
          </div>
        )}
      </section>

      <TransactionSheet
        open={sheetOpen}
        onClose={() => setSheetOpen(false)}
        initial={sheetInitial}
        onSaved={onSaved}
      />
      <ConfirmVoiceSheet
        open={reviewOpen}
        onClose={() => setReviewOpen(false)}
        payload={reviewPayload}
        onSaved={onSaved}
      />
    </div>
  );
}
